import classNames from 'classnames';
import _, { toNumber } from 'lodash';
import { DateTime } from 'luxon';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import KeyCode from '../../types/enum/KeyCode';
import Validator from '../../utilities/Validator';
import Select from '../Select/Select';
import './TimeField.scss';
import ITimeFieldProps from './TimeFieldProps';
import TimeTotalField from './TimeTotalField';

const TimeField = ({
  value,
  inputs,
  minuteStep,
  disabled,
  placeholder,
  error,
  onChange,
  onFocus,
  onBlur,
  onMeridiemBlur,
  meridiemEnabled,
  errorOnEmpty,
  className,
}: ITimeFieldProps) => {
  const [period, setPeriod] = useState<'AM' | 'PM' | null>(
    meridiemEnabled ? (inputs && inputs.period ? inputs.period : 'AM') : null
  );
  const [inputValue, setInputValue] = useState<string>(inputs && inputs.inputValue ? inputs.inputValue : '');
  const [hasError, setHasError] = useState(false);
  const [inputIsDirty, setInputIsDirty] = useState(false);
  const input = useRef<HTMLInputElement>();
  const select = useRef<HTMLSelectElement | null>();
  const [currentValue, setCurrentValue] = useState<DateTime | null>();
  const inputIsCached = useRef(false);
  const inputRef = useRef<HTMLInputElement>();
  const inputIsFocused = document.activeElement === inputRef.current;

  useEffect(() => {
    if (inputIsFocused) {
      return;
    }
    if (value?.isValid) {
      // Need to take the value that's passed, check the input value's parsed value and make sure they're not the same before auto filling
      const formattedCurrent = format(inputValue);
      if (formattedCurrent) {
        const currentInputValueValue = inputValue ? toMilitaryTime(formattedCurrent, period) : null;
        if (currentInputValueValue !== value.toFormat('HH:mm')) {
          const timeObj = parseMilitaryTime(value.toFormat('HH:mm'));
          setPeriod(timeObj.period);
          setInputValue(timeObj.time);
        }
      } else {
        const timeString = value.toFormat('HH:mm');
        const timeObj = parseMilitaryTime(timeString);
        setPeriod(timeObj.period);
        setInputValue(timeObj.time);
      }
      setCurrentValue(value);
    } else if (value === null) {
      if (!inputIsCached.current) {
        setInputValue('');
      }
      // Don't set it if we went from having something to not having something -- Need to persist date information
      if (!currentValue) {
        setCurrentValue(DateTime.local().startOf('day'));
      }
    } else {
      // tslint:disable-next-line: no-console
      console.error('Value must be a valid date time');
      setHasError(true);
      setInputIsDirty(true);
    }
  }, [value?.toSeconds()]);

  useEffect(() => {
    if (inputs) {
      if (inputs.inputValue) {
        handleInputChange(inputs.inputValue);
      }

      if (inputs.period) {
        setPeriod(inputs.period);
      }
    }
  }, [inputs]);

  function convertFormattedStringToHourMinute(formatted: string) {
    const [hour, minute] = formatted.split(':').map(toNumber);
    return { hour, minute };
  }

  function applyFormattedStringToCurrentValue(formatted: string) {
    return currentValue?.set(convertFormattedStringToHourMinute(formatted)) ?? null;
  }

  function handleChange(val: string | null) {
    let formatted = null;
    const isValid = val !== null && !_.isEmpty(val);

    let newValue: DateTime | null = value;
    if (isValid && val) {
      const timeObj = parseMilitaryTime(val);
      formatted = timeObj.period ? timeObj.time + ' ' + timeObj.period : timeObj.time;
      if (isValid && currentValue) {
        newValue = applyFormattedStringToCurrentValue(val);
        setCurrentValue(newValue);
      } else {
        newValue = null;
      }
    } else {
      newValue = null;
    }

    onChange({
      value: isValid ? newValue : null,
      formatted,
      isValid: newValue !== null,
    });

    const err = !isValid && (errorOnEmpty || (!errorOnEmpty && val !== ''));

    if (err !== hasError) {
      setHasError(err);
    }
  }

  function handleInputChange(val: string) {
    let newValue = val === '' && !errorOnEmpty ? '' : null;

    if (isValidInputText(val)) {
      newValue = toMilitaryTime(format(val)!, period);
    }

    if (!(val.length > 4 && !isValidInputText(val))) {
      setInputValue(val);
      handleChange(newValue);
    }
  }

  function handlePeriodChange(meridiem: string) {
    const newValue =
      value !== null ? toMilitaryTime(parseMilitaryTime(value.toFormat('HH:mm')).time, meridiem as 'AM' | 'PM') : null;
    setPeriod(meridiem as 'AM' | 'PM');
    handleChange(newValue ?? (inputValue === '' && !errorOnEmpty ? '' : null));
  }

  function handlePeriodOnKeyDown(event: KeyboardEvent) {
    const keyCode = event.keyCode;

    if (keyCode === KeyCode.ARROW_UP || keyCode === KeyCode.ARROW_DOWN) {
      event.preventDefault();
      handlePeriodChange(period === 'AM' ? 'PM' : 'AM');
    } else if (keyCode === KeyCode.ARROW_LEFT) {
      event.preventDefault();
      if (input.current) {
        input.current.focus();
      }
    }
  }

  // Add num hours to state.value
  function adjustHours(num: number) {
    // tslint:disable-next-line: prefer-const
    const standardTime = parseStandardTime(value === null ? '' : value.toFormat('HH:mm'));
    const { minutes } = standardTime;
    let { hours } = standardTime;

    hours += num;
    if (hours > 23) {
      hours -= 24;
    } else if (hours < 0) {
      hours += 24;
    }

    const hoursString = addLeadingZeros(hours);
    const minutesString = addLeadingZeros(minutes);
    const val = hoursString + ':' + minutesString;
    const timeObj = parseMilitaryTime(val);

    setInputValue(timeObj.time);
    setPeriod(timeObj.period);
    handleChange(val);
  }

  function adjustMinutes(num: number) {
    // tslint:disable-next-line: prefer-const
    const standardTime = parseStandardTime(value === null ? '' : value.toFormat('HH:mm'));
    const { hours } = standardTime;
    let { minutes } = standardTime;

    minutes += num;
    if (minutes >= 60) {
      minutes -= 60;
    } else if (minutes < 0) {
      minutes += 60;
    }

    const hoursStr = addLeadingZeros(hours);
    const minutesStr = addLeadingZeros(minutes);
    const val = hoursStr + ':' + minutesStr;
    const timeObj = parseMilitaryTime(val);

    setInputValue(timeObj.time);
    setPeriod(timeObj.period);
    handleChange(val);
  }

  function addLeadingZeros(num: number) {
    const numStr = num.toString();
    return numStr.length < 2 ? '0' + numStr : numStr;
  }

  function isValidInputText(str: string) {
    return format(str) !== null;
  }

  // converts standard time format to military format (1:00 PM to 13:00)
  function toMilitaryTime(standardTime: string, meridiem: 'AM' | 'PM' | null) {
    const split = standardTime.split(':');
    let hours = +split[0];
    let militaryTime;

    if (meridiem === 'AM' && hours === 12) {
      hours -= 12;
    }

    if (meridiem === 'PM' && hours !== 12) {
      hours += 12;
    }
    militaryTime = hours + ':' + split[1].split(' ')[0];

    if (militaryTime.length === 4) {
      militaryTime = '0' + militaryTime;
    }

    return militaryTime;
  }

  // converts military time an object containing the formatted standard time and period
  function parseMilitaryTime(timeStr: string): { time: string; period: 'AM' | 'PM' | null } {
    if (meridiemEnabled) {
      const split = timeStr.split(':');
      let hours = Number.parseInt(split[0], 10);

      let meridiem: 'AM' | 'PM' = 'AM';

      if (hours > 11) {
        hours = hours - 12;
        meridiem = 'PM';
      }

      if (hours === 0) {
        hours = 12;
      }

      return { time: hours + ':' + split[1], period: meridiem };
    } else {
      return { time: timeStr, period: null };
    }
  }

  // parses time string to an objects containing hours and minutes
  function parseStandardTime(timeStr: string) {
    const result = {
      hours: 0,
      minutes: 0,
    };

    if (Validator.isHoursMinutesFormat(timeStr)) {
      const split = timeStr.split(':');
      result.hours = parseInt(split[0], 10);
      result.minutes = parseInt(split[1], 10);
    }
    return result;
  }

  // Formats the number string by adding the colon and making assumptions about
  // missing digits (i.e. 8 == 8:00). Returns false if the string can't be formatted.
  function format(timeStr: string) {
    let res: string | null = timeStr.replace(/^0+/, ''); // remove leading zeros

    if (!Validator.isHoursMinutesFormat(res, false)) {
      if (/^[1-9]{1}$/.test(res)) {
        // single number
        res = `${res}:00`;
      } else if (/^1[0-2]{1}$/.test(res)) {
        // 2 numbers (10,11,12)
        res = `${res}:00`;
      } else if (/^[1-9]{1}[1-5]{1}$/.test(res) && meridiemEnabled) {
        // 2 numbers
        res = res.slice(0, 1) + ':' + res.slice(1) + '0';
      } else if (/^[1-9]{1}[0-3]{1}$/.test(res) && !meridiemEnabled) {
        // 2 numbers 24 hour 21,22,23
        res = `${res}:00`;
      } else if (/^[1-9]{1}[4-5]{1}$/.test(res) && !meridiemEnabled) {
        res = res.slice(0, 1) + ':' + res.slice(1) + '0';
      } else if (/^[1-9]{1}[0-5]{1}[0-9]$/.test(res)) {
        // 3 numbers
        res = res.slice(0, 1) + ':' + res.slice(1);
      } else if (/^1[0-2]{1}[0-5]{1}[0-9]{1}$/.test(res)) {
        // 4 numbers
        res = res.slice(0, 2) + ':' + res.slice(2);
      } else if (/^1[0-2]{1}[0]{1,4}$/.test(res)) {
        // trailing zeros (10, 11, 12)
        res = res.slice(0, 2) + ':00';
      } else if (/^[1-9]{1}[0]{1,4}$/.test(res)) {
        // trailing zeros
        res = res.slice(0, 1) + ':00';
      } else if (/^:[0-5]{1}[0-9]{1}$/.test(res)) {
        // :30 => 0:30
        res = '0' + res;
      } else if (/^:[0-5]{1}$/.test(res)) {
        // :3 => 0:30
        res = '0' + res + '0';
      }

      if (!Validator.isHoursMinutesFormat(res)) {
        res = null;
      }
    }

    return res;
  }

  function assignSelect(element: HTMLSelectElement | null) {
    select.current = element;
  }

  function handleOnChange(val: string | null) {
    if (val !== null) {
      inputIsCached.current = format(val) === null && val !== '';
      handleInputChange(val);
    }
  }

  function handleInputBlur(val: string) {
    const isValidInput = isValidInputText(val);
    if (isValidInput) {
      setInputValue(format(val)!);
    }

    setInputIsDirty(true);

    if (onBlur) {
      if (isValidInput) {
        const adjustedTime = toMilitaryTime(format(val)!, period);
        onBlur(applyFormattedStringToCurrentValue(adjustedTime));
      } else {
        onBlur(null);
      }
    }
  }

  function handlePeriodBlur() {
    onMeridiemBlur?.(currentValue ?? null);
  }

  const classes = classNames(
    'time-field',
    {
      'meridiem-enabled': meridiemEnabled,
    },
    className
  );

  return (
    <div className={classes}>
      <TimeTotalField
        tabIndex={0}
        value={inputValue}
        disabled={disabled}
        placeholder={placeholder}
        minuteStep={minuteStep}
        adjustMinutes={adjustMinutes}
        adjustHours={adjustHours}
        onFocus={onFocus}
        onBlur={handleInputBlur}
        error={(inputIsDirty && hasError) || error}
        handleChange={handleOnChange}
        inputs={inputs}
        forwardRef={(ref) => (inputRef.current = ref)}
      />
      {meridiemEnabled && (
        <Select
          selectRef={assignSelect}
          disabled={disabled}
          value={period}
          onChange={handlePeriodChange}
          onKeyDown={handlePeriodOnKeyDown}
          onBlur={handlePeriodBlur}
          options={[
            { label: 'AM', value: 'AM' },
            { label: 'PM', value: 'PM' },
          ]}
          hasArrow={false}
        />
      )}
    </div>
  );
};

TimeField.defaultProps = {
  value: '',
  period: 'AM',
  minuteStep: 5,
  disabled: false,
  placeholder: 'H:MM',
  meridiemEnabled: true,
  errorOnEmpty: true,
};

export default TimeField;
